import { api } from '@/api/client';
import type { Role } from '@/types/api';

// Telemetry V1: POST /ui/sessions → session_id, потом POST /ui/events батчами по 10 событий каждые 5 сек.
//
// Sprint 5 wire-up. Контракт согласован с app/ui_telemetry.py:
//   UiSessionCreate: role + project_id + client_meta
//   UiEventIn: session_id + event_type + client_ts + ... + payload
//   Rate limit: 100 событий/мин на сессию (см. ui_telemetry.py).

interface SessionState {
  session_id: string | null;
  role: Role | null;
  project_id: string | null;
}

interface QueuedEvent {
  id: string; // idempotency key
  event_type: string;
  client_ts: string;
  project_id?: string;
  snapshot_id?: string;
  defect_axis?: 'S' | 'E' | 'unknown';
  target_entity_type?: string;
  target_entity_id?: string;
  duration_ms?: number;
  payload?: Record<string, unknown>;
}

const state: SessionState = {
  session_id: null,
  role: null,
  project_id: null,
};

const queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
const FLUSH_INTERVAL_MS = 5000;
const BATCH_SIZE = 20;

/** Стартовать UI-сессию. Вызывается из bootstrap auth когда user определён. */
export async function startSession(role: Role, project_id?: string): Promise<void> {
  if (state.session_id && state.role === role && state.project_id === (project_id || null)) {
    return; // уже активна
  }
  if (state.session_id) {
    await endSession('role_switch').catch(() => { /* swallow */ });
  }
  try {
    const res = await api.post<{ session_id: string }>('/ui/sessions', {
      role,
      project_id: project_id || undefined,
      client_meta: {
        ua: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        ts: new Date().toISOString(),
      },
    });
    state.session_id = res.session_id;
    state.role = role;
    state.project_id = project_id || null;
    schedule();
    track('session_started', { project_id, payload: { role } });
  } catch {
    // Telemetry — best effort. Логируем но не блокируем UX.
  }
}

/** Завершить сессию (logout, role-switch). */
export async function endSession(reason: string = 'logout'): Promise<void> {
  const sid = state.session_id;
  if (!sid) return;
  state.session_id = null;
  state.role = null;
  state.project_id = null;
  await flush();
  try {
    await api.post(`/ui/sessions/${sid}/end`, { reason });
  } catch { /* swallow */ }
}

/** Записать событие. Запоминается в очередь и сбрасывается батчем. */
export function track(event_type: string, extra: Partial<QueuedEvent> & { payload?: Record<string, unknown> } = {}): void {
  if (!state.session_id) return; // нет активной сессии — skip
  queue.push({
    id: crypto.randomUUID(),
    event_type,
    client_ts: new Date().toISOString(),
    project_id: extra.project_id || state.project_id || undefined,
    snapshot_id: extra.snapshot_id,
    defect_axis: extra.defect_axis,
    target_entity_type: extra.target_entity_type,
    target_entity_id: extra.target_entity_id,
    duration_ms: extra.duration_ms,
    payload: extra.payload || {},
  });
  if (queue.length >= BATCH_SIZE) {
    flush();
  } else {
    schedule();
  }
}

function schedule(): void {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => { flush(); }, FLUSH_INTERVAL_MS);
}

async function flush(): Promise<void> {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0 || !state.session_id) return;
  const batch = queue.splice(0, BATCH_SIZE).map((e) => ({ ...e, session_id: state.session_id }));
  try {
    await api.post('/ui/events', { events: batch });
  } catch {
    // Не возвращаем события в очередь — иначе при долгой недоступности backend очередь распухнет.
    // Sprint 5+: persistent queue в IndexedDB.
  }
}

// Flush при закрытии вкладки/перезагрузке
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (queue.length > 0 && state.session_id && 'sendBeacon' in navigator) {
      const batch = queue.splice(0, queue.length).map((e) => ({ ...e, session_id: state.session_id }));
      const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
      navigator.sendBeacon('/ui/events', blob);
    }
  });
}
